import BatchViewHtml from "./BatchViewHtml.js";
import PlateView from "../../js/view/PlateView.js";
import PlateList from "../../js/entities/PlateList.js";
import PlateViewModel from "../../js/model/PlateViewModel.js";
import {SelectionHelper} from "../../js/components/visualization/VisualizationDataSelection.js";
import {VTableComponent} from "../../js/components/visualization/table_v2/VTableComponent.js";
import {DataTable, TableRowComparator, VizData} from "../../js/components/visualization/TableRows.js";
import InjectionRow from "../../js/components/visualization/InjectionRow.js";
import PivotedInjection from "../../js/components/visualization/PivotedInjection.js";
import TableColumns from "../../js/components/visualization/TableColumns.js";
import TableColumn from "../../js/components/visualization/TableColumn.js";
import InjectionMetaJson from "../../js/entities/InjectionMetaJson.js";
import {compareStringsByCodePoints} from "../../js/util/CommonUtils.js";

export default class BatchViewCmp {
    // If there are more than 'PLATES_THRESHOLD' plates in the batch, then we want to show the component
    // as a table.
    // This threshold includes all plates (separate and stacked)
    static PLATES_THRESHOLD= 4;
    static PLATES_VIEW_MODE_NAME =  'plates';
    static TABLE_VIEW_MODE_NAME= 'table';

    /** @type {BatchViewHtml} */
    #html;
    /** @type {PlateListCmp} */
    #platesCmp;
    /** @type {VTableComponent} */
    #tableCmp;
    /**
     * activeView: what the User sees, plate(s) or table, possible values are defined in 'PLATES_VIEW_MODE_NAME' and
     *              TABLE_VIEW_MODE_NAME constants.
     * isLoading: if true, the 'dancing bars' animation is shown; otherwise, the actual data.
     * @type {{ activeView: string, isLoading: boolean, isVisible: boolean, isInjectionPage: boolean }}
     */
    #displayOptions;
    #activeInjectionId;
    #onPlateOrTableSelectionChange = () => {
    };
    #onViewModeChange = () => {
    };
    #onInjectionClick = () => {
    };
    #onTableRowClick = () => {
    };
    #onInjectionHover = () => {
    };

    /**
     *
     * @param {PlateListCmp} platesCmp
     * @param {VTableComponent} tableCmp
     *
     */
    constructor(platesCmp, tableCmp) {
        this.#platesCmp = platesCmp;
        this.#tableCmp = tableCmp;

        this.#platesCmp.onPlateCellClick((
            /** @type {{injectionId: string, plateId: string, altKey: boolean, shiftKey: boolean}}*/ data) => {

            if (this.#displayOptions.isInjectionPage && !data.shiftKey){
                this.#onInjectionClick(data);
            } else {
                // If we click on the same injection with shift we want to remove all injections from comparison
                if (data.shiftKey && this.#activeInjectionId === data.injectionId)
                    this.#onPlateOrTableSelectionChange({added: [], removed: undefined})
                else {
                    const event = SelectionHelper
                        .createChangeSelectionEvent(new Map([[data.injectionId, null]]), data);
                    this.#onPlateOrTableSelectionChange(event);
                }

            }
        });
        this.#platesCmp.onSelectionChange((data) => {
            this.#onPlateOrTableSelectionChange(data);
        });
        this.#platesCmp.onInjectionHover((injectionId, isHovered) => {
            this.#onInjectionHover(injectionId, isHovered);
        });
        this.#tableCmp.onRowHover((injectionId, isHovered) => {
            this.#onInjectionHover(injectionId, isHovered);
        });
        this.#tableCmp.onRowClick( (rowIndex, cellIndex, rowObjectId, rowSelector, evt)=> {
            this.#onTableRowClick(rowIndex, cellIndex, rowObjectId, rowSelector, evt);
        });
        // Some parts of the table component were designed using the Event Bus concept.
        // Although we're not using the Event Bus in our new code (at the time of writing),
        // I don't think we should refactor the following method, as it could require full regression testing.
        this.#tableCmp.on( 'selection-changed', (data)=>{
            this.#onPlateOrTableSelectionChange(data);
        });
    }

    /**
     * @param {HTMLElement} rootElement
     * @param {{
     *  isLoading: boolean,
     *  activeView: string,
     *  }} [displayOptions]
     */
    init(rootElement, displayOptions) {
        if (this.#html)
            throw new Error("BatchViewCmp is already initialized");
        this.#displayOptions = Object.assign({}, displayOptions);
        this.#html = new BatchViewHtml(rootElement, this.#displayOptions);
        this.#html.onChangeView(()=>{
           const targetView = this.#displayOptions.activeView === BatchViewCmp.TABLE_VIEW_MODE_NAME ?
               BatchViewCmp.PLATES_VIEW_MODE_NAME : BatchViewCmp.TABLE_VIEW_MODE_NAME;
           this.setActiveView(targetView);
           this.#onViewModeChange(targetView);
        });
    }

    /**
     * Renders component in the DOM
     */
    show() {
        this.#html.show();
    }

    /**
     * @return {boolean}
     */
    isInitialized() {
        return this.#html != null;
    }

    /**
     * Shows/hides 'loading' animation
     * @param {boolean} isLoading
     */
    setLoading(isLoading) {
        this.#displayOptions.isLoading = isLoading;
        this.#html.setDisplayOptions(this.#displayOptions);
    }
    /** @return {boolean} */
    isLoading(){
        return this.#displayOptions.isLoading;
    }

    /** @param {{plate:PlateMeta, injections:InjectionMetaJson[]|InjectionRow[]}[]} plateToInjections */
    setData(plateToInjections) {
        this.#displayOptions.isVisible = plateToInjections.reduce((acc, val) => acc + val.injections.length, 0) > 0;
        this.#html.setDisplayOptions(this.#displayOptions);
        this.#renderPlates(plateToInjections);
        this.#renderTable(plateToInjections);
    }

    /**
     * @param {string} view
     */
    setActiveView(view) {
        if ([BatchViewCmp.PLATES_VIEW_MODE_NAME, BatchViewCmp.TABLE_VIEW_MODE_NAME].indexOf(view) < 0)
            throw new Error("Unknown view mode: " + view);
        this.#displayOptions.activeView = view;
        this.#html.setDisplayOptions(this.#displayOptions);
        if (view === BatchViewCmp.TABLE_VIEW_MODE_NAME && this.#tableCmp.isRendered()) {
            this.#tableCmp.setActiveRow(this.#tableCmp.getActiveRowIndex());
        }
    }
    convertCoordinatesToInjections(literalCoordinates) {
        return  this.#platesCmp.updateSelectionByLiteralCoordinates(literalCoordinates);
    }
    changePlateLevel(data) {
        this.#platesCmp.changePlateLevel(data);
    }

    navigationKeyPressed(data) {
        if (this.#displayOptions.activeView === BatchViewCmp.PLATES_VIEW_MODE_NAME) {
            this.#platesCmp.navigationKeyPressed(data);
        } else {
            this.#tableCmp.navigationKeyPressed(data, (injectionId)=>{
                this.#onInjectionClick({injectionId});
            });
        }
    }

    onSelectionChange(callback) {
        this.#onPlateOrTableSelectionChange = callback;
    }
    onViewModeChange(callback) {
        this.#onViewModeChange = callback;
    }
    onInjectionClick(callback) {
        this.#onInjectionClick = callback;
    }
    onTableRowClick(callback) {
        this.#onTableRowClick = callback;
    }
    onInjectionHover(callback) {
        this.#onInjectionHover = callback;
    }
    setActiveInjection(injectionId) {
        this.#activeInjectionId = injectionId;
        this.#platesCmp.setActiveInjection(injectionId);
        const findIndex = this.#tableCmp.vizData._getActualRows().findIndex(r=>r.id === injectionId);
        this.#tableCmp.setActiveRow(findIndex);
    }
    getFirstHighlightedInjectionId(){
        return this.#platesCmp.getFirstHighlightedInjectionId();
    }
    getFirstInjectionId(){
        return this.#platesCmp.getFirstInjectionId();
    }

    getAllInjectionIds() {
        return this.#platesCmp.getAllInjectionIds();
    }

    getAllInjectionIdsFromPlateWithInjection(injectionId) {
        return this.#platesCmp.getAllInjectionIdsFromPlateWithInjection(injectionId);
    }

    /**
     * @return {string}
     */
    getHighlightedWellsAsString() {
        let res = '';
        for (let model of this.#platesCmp.plateViewModels)
            res += `[${model.getHighlightedWellsAsString()}];`;
        return res;
    }

    /** @param {Map<string, {injection: InjectionJson, color: string}>} selectedInjections */
    toggleComparingHighlight(selectedInjections) {
        // Both components (plate and table) use one VisualizationDataSelection,
        // so we can add selected injections only ones (in this case we add in plates cmp)
        this.#platesCmp.setComparedInjections(selectedInjections);
        this.#platesCmp.updateCompareSelection()
        this.#tableCmp.updateCompareSelection()
    }

    /** @param {number|null} index */
    setLastClickedRowId(index){
        this.#tableCmp.setLastClickedRowId(index)
    }

    /** @param {{plate:PlateMeta, injections:InjectionMetaJson[]|InjectionRow[]}[]} plateToInjections */
    #renderPlates(plateToInjections) {
        const plateViewRootEl = this.#html.getPlateViewRootElement();
        for (const {plate, injections} of plateToInjections) {
            const view = new PlateView(plateViewRootEl, plate,
                {isInjectionPage: this.#displayOptions.isInjectionPage});
            const plateList = PlateList.create(plate, injections);
            const plateModel = new PlateViewModel(plateList, view,
                {showActiveWell: this.#displayOptions.isInjectionPage});
            this.#platesCmp.addPlate(plateModel);
        }
    }
    /** @param {{plate:PlateMeta, injections:InjectionMetaJson[]|InjectionRow[]}[]} plateToInjections */
    #renderTable(plateToInjections) {
        const rows = [];
        for (const {injections} of plateToInjections) {
            for (const injection of injections) {
                if (injection instanceof InjectionRow)
                    rows.push(injection);
                else if (injection instanceof InjectionMetaJson)
                    rows.push(BatchViewCmp.#mapInjectionToTableRow(injection));
                else
                    throw new Error("Injection type: " + injection.constructor.name +
                        " is not supported in " + JSON.stringify(injection));
            }
        }
        const vizData = new VizData(new DataTable(rows, BatchViewCmp.#TABLE_COLS));
        vizData.setSorting(new TableRowComparator((a, b) => {
            return compareStringsByCodePoints(a.id, b.id);
        }));
        const tableViewRootElement = this.#html.getTableViewRootElement();
        this.#tableCmp.render(
            tableViewRootElement,
            vizData,
            {
                tableHeightPx: 168,
                isInjectionPage: this.#displayOptions.isInjectionPage
            }
        );
    }

    /**
     * @param {InjectionMetaJson} injection
     * @return {InjectionRow}
     */
    static #mapInjectionToTableRow(injection){
        return new InjectionRow({
            injection: new PivotedInjection({
                id: injection.id,
                name: injection.getDisplayName(),
                pos: injection.getOnPlatePosition(),
                inletMethod: injection.getMethodName(),
                instrumentName: injection.getInstrumentName(),
                bed: injection.getBed(),
            }),
        })
    }

    static #TABLE_COLS = new TableColumns([
        new TableColumn("Injection", 'injection.name'),
        new TableColumn("Bed", 'injection.bed'),
        new TableColumn("Plate location", 'injection.well'),
        new TableColumn("Inlet method", 'injection.inletMethod'),
        new TableColumn("Instrument name", 'injection.instrumentName'),
    ]);
}